/**
 * Route gate for MSI3 / AcuityDrill teaching:
 * - loading → Loading page
 * - unsigned → auth with next=
 * - unverified email (and not dev-auth) → verify prompt
 * - no profile → onboarding
 * - profile exists → children
 */
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import Loading from "components/Loading";
import { useAuth } from "hooks/useAuth";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import firebase from "firebase/compat/app";
import { useMsi3LearnerProfile } from "../msi3LearnerProfile";
import Msi3LearnerOnboarding from "./Msi3LearnerOnboarding";

export default function RequireMsi3Learner({ children }) {
  const { user, userData } = useAuth();
  const { loading, hasProfile, refresh } = useMsi3LearnerProfile();
  const location = useLocation();

  if (loading) return <Loading page />;

  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
  }

  if (!user.emailVerified && !isDevAuthFirestoreUser(userData)) {
    const resend = async () => {
      try {
        await firebase.auth().currentUser?.sendEmailVerification();
      } catch {
        /* ignore */
      }
    };
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", py: 6, px: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Confirm your email
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please verify your email address before using MorningReport Cases.
          Check your inbox for a verification link.
        </Alert>
        <Button variant="contained" onClick={() => void resend()}>
          Resend verification email
        </Button>
      </Box>
    );
  }

  if (!hasProfile) {
    return <Msi3LearnerOnboarding refresh={refresh} />;
  }

  return children;
}
